/**
 * The skill-FX detail layer (C0-SKILLFX, docs/research/C0_VFX.md §10): drawn on top of each move's ink template, in
 * three beats —
 * - anticipation: element motes drawn into the striking hand and a glint in the move KIND's accent that peaks on the
 *   "now" frame (attack vermilion, break amber, power gold: the duel's tell stays the kind colour);
 * - impact (+2 frames after the core flash): hit sparks by material, the art's motif burst, a ground ring;
 * - dissipation: motifs drift and dither away, ground marks dry in dither steps.
 * Plus per move: a sub-frame smear between the wind-up and the contact, speed streaks on fast moves, a body aura for
 * buffs and heals, a lifesteal stream, pressure-point ripples, a travelling shockwave.
 * Counts scale with the blow's weight (data/hitWeight.ts: light/medium/heavy) and the graphics tier (`quality`).
 * Everything is in art pixels local to the caster (`P`), deterministic (hash noise), hard pixels: what clings to a
 * figure steps on the figures' grid, what belongs to the world on the world grid.
 */
import { hitWeightOf } from '@nine/shared';
import { RES } from '../brush';
import { clamp01, easeIn, easeOut, hash2, span } from '../noise';
import { INK_PALETTES, type InkKind } from '../palette';
import { foeX, P, type InkLayer, type InkScene } from '../samples';
import { canvas, ctx2d } from '../../pixel';
import type { MoveDetail } from './moves';
import { RAMPS, type Ramp, type RampId } from './ramps';
import { drawAuraBloom, drawHitBloom, drawSpinBloom } from './bloom';
import { ANGLES, cellOf, dot, drawMotif, snap, type Grid, type Motif } from './sprites';

export type DetailWeight = 0 | 1 | 2;

/**
 * The scale pass (lane C0-VFX-PREP, toward docs/art/auras-ref and skills-ref, which run 2–3× our old reach round the
 * body): `S` multiplies every radius, reach and travel of this layer; motifs grow by `MS` cells (sprites stay on the
 * same pixel grids, nothing is magnified).
 */
export const S = 2.2;
const MS = 1.5;

/** The blow's weight class as 0 light / 1 medium / 2 heavy (a landed, unguarded hit of the move). */
export function weightOfMove(skillId: string): DetailWeight {
  const w = hitWeightOf(skillId, 'hit');
  return w?.cls === 'heavy' ? 2 : w?.cls === 'medium' ? 1 : 0;
}

// ── quality (the tier's budget) ──

let quality = 1;
/** 1 full counts; 0.5 on the low tiers (graphics.ts sets it; the lab can preview it). */
export function setDetailQuality(q: number): void {
  quality = q;
}
export const detailQuality = (): number => quality;
/** A count by weight, scaled by the tier's quality, at least one. */
const N = (base: readonly [number, number, number], w: DetailWeight): number => Math.max(1, Math.round(base[w] * quality));

/** Particles drawn this frame (the lab's budget check). */
let drawn = 0;
export const detailDrawn = (): number => drawn;
export const resetDetailDrawn = (): void => {
  drawn = 0;
};

// ── local points ──

/** The striking hand / blade root, the foe's chest and feet, local to the caster. */
const HAND: readonly [number, number] = [9, -40];
const foeChest = (sc: InkScene): [number, number] => [foeX(sc), sc.tdy - 42];

function at(g: CanvasRenderingContext2D, sc: InkScene, m: Motif, ramp: Ramp, size: number, grid: Grid, lx: number, ly: number, rot = 0, fade = 0): void {
  const [x, y] = P(sc, lx, ly);
  const r = sc.flip ? ANGLES - rot : rot;
  drawMotif(g, m, ramp, Math.max(1, Math.round(size * MS)), grid, x, y, r, fade);
  drawn++;
}
function px(g: CanvasRenderingContext2D, sc: InkScene, col: string, lx: number, ly: number, grid: Grid, cells = 1): void {
  const [x, y] = P(sc, lx, ly);
  dot(g, col, x, y, grid, cells);
  drawn++;
}
/** Dither fade: whether cell/particle `i` is gone at fade `f` (0 none, 1 all), in four steps. */
export const gone = (i: number, f: number, seed: number): boolean => f > 0 && hash2(i, 3, seed) < Math.floor(f * 4) / 4 + (f >= 1 ? 1 : 0);

// ── cached pictures (smears, rings, fans): one drawImage a frame ──

export interface Pic {
  c: HTMLCanvasElement;
  x0: number;
  y0: number;
}
const PICS = new Map<string, Pic>();
/** A picture over art box [x0, x1) × [y0, y1), painted cell by cell (`set`) at the figures' resolution. */
export function pic(key: string, x0: number, y0: number, x1: number, y1: number, paint: (set: (x: number, y: number, col: string, cell: number) => void) => void): Pic {
  let p = PICS.get(key);
  if (p) return p;
  const c = canvas(Math.max(1, Math.ceil((x1 - x0) * RES)), Math.max(1, Math.ceil((y1 - y0) * RES)));
  const g = ctx2d(c);
  paint((x, y, col, cell) => {
    g.fillStyle = col;
    g.fillRect(Math.round((x - x0) * RES), Math.round((y - y0) * RES), Math.max(1, cell * RES), Math.max(1, cell * RES));
  });
  p = { c, x0, y0 };
  PICS.set(key, p);
  return p;
}
export function drawPic(g: CanvasRenderingContext2D, sc: InkScene, p: Pic, lx: number, ly: number): void {
  const [X, Y] = P(sc, lx, ly);
  const x = Math.round(X * RES) / RES;
  const y = Math.round(Y * RES) / RES;
  const w = p.c.width / RES;
  const h = p.c.height / RES;
  drawn++;
  if (!sc.flip) {
    g.drawImage(p.c, x + p.x0, y + p.y0, w, h);
    return;
  }
  g.save();
  g.translate(x, y);
  g.scale(-1, 1);
  g.drawImage(p.c, p.x0, p.y0, w, h);
  g.restore();
}

/** A thin pixel ellipse ring (rx, ry art px) in two tones, dithered by `fade` (0–3), world grid. */
export function ringPic(R: Ramp, rx0: number, ry0: number, fade: number, thick = 1): Pic {
  // Even radii only, so a growing ring reuses half as many cached pictures.
  const rx = Math.max(2, Math.round(rx0 / 2) * 2);
  const ry = Math.max(2, Math.round(ry0 / 2) * 2);
  const c = cellOf('world');
  return pic(`ring|${R[0]}|${rx}|${ry}|${fade}|${thick}|${c}`, -rx - 2, -ry - 2, rx + 2, ry + 2, (set) => {
    const n = Math.max(12, Math.round((rx + ry) * 3));
    for (let i = 0; i < n; i++) {
      if (fade > 0 && (i * 5 + (i >> 1)) % 4 < fade) continue;
      const a = (i / n) * Math.PI * 2;
      const x = Math.round(Math.cos(a) * rx);
      const y = Math.round(Math.sin(a) * ry);
      set(x, y, y < 0 ? R[3] : R[4], c);
      if (thick > 1) set(x, y + c, R[1], c);
    }
  });
}

/** A dried-down ramp for stains and lingering marks. */
const STAIN = (R: Ramp): Ramp => [R[0], R[1], R[1], R[1], R[2]];

// ── 1. anticipation: motes and the kind glint ──

/** The kind's tell colours (the duel's language): accent, light, dark. */
const tellOf = (kind: InkKind): readonly [string, string, string] => {
  const p = INK_PALETTES[kind];
  return [p.acc, p.accLit, p.accDark];
};

/**
 * Motes drawn into the hand over a wind-up (`u` 0–1) and the kind glint over its last quarter ("now"). Half the motes
 * are in the kind accent, half in the element's light tone, so the tell reads as the type first.
 */
export function drawTell(g: CanvasRenderingContext2D, u: number, sc: InkScene, d: MoveDetail, kind: InkKind, w: DetailWeight, target: readonly [number, number] = HAND): void {
  if (u < 0 || u > 1) return;
  const R = RAMPS[d.ramp];
  const [acc, lit] = tellOf(kind);
  const n = N([10, 14, 20], w);
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + hash2(i, 1, 501) * 0.8 + u * 2.2;
    const r = (12 + 9 * hash2(i, 2, 501)) * S * (1 - easeIn(u));
    if (r < 1) continue;
    px(g, sc, i % 2 ? R[4] : acc, target[0] + Math.cos(a) * r, target[1] + Math.sin(a) * r * 0.8, 'fig', r > 20 ? 2 : 3);
  }
  if (u < 0.72) return;
  // The glint: a four-point star, white core, light arms, accent tips; grows to the "now" frame.
  const k = (u - 0.72) / 0.28;
  const c = cellOf('fig');
  const arm = Math.round(2 + (6 + 2 * w) * k);
  const [X, Y] = P(sc, target[0], target[1]);
  const x = snap(X, 'fig');
  const y = snap(Y, 'fig');
  for (let i = 1; i <= arm; i++) {
    g.fillStyle = i === arm ? acc : lit;
    g.fillRect(x - i * c, y, c, c);
    g.fillRect(x + i * c, y, c, c);
    g.fillRect(x, y - i * c, c, c);
    g.fillRect(x, y + i * c, c, c);
  }
  g.fillStyle = '#ffffff';
  g.fillRect(x - c, y - c, c * 3, c * 3);
  drawn += 5;
}

// ── 2. smears ──

/** A smear frame (0 leading half, 1 full, 2 thinning tail) of a shape, cached. */
function smearPic(shape: 'arc' | 'rise' | 'line' | 'ring', R: Ramp, D: number, frame: 0 | 1 | 2): Pic {
  const c = cellOf('fig');
  const Db = Math.round(D / 8) * 8;
  return pic(`smear|${shape}|${R[0]}|${Db}|${frame}|${c}`, -96, -130, Math.max(110, Db + 30), 40, (set) => {
    const s0 = frame === 2 ? 0.45 : 0;
    const s1 = frame === 0 ? 0.55 : 1;
    const thin = frame === 2 ? 0.5 : 1;
    if (shape === 'arc' || shape === 'rise') {
      const R0 = 25 * S;
      const n = 200;
      for (let i = 0; i <= n; i++) {
        const s = i / n;
        if (s < s0 || s > s1) continue;
        const q = shape === 'arc' ? s : 1 - s;
        const a = -1.9 + q * 2.8;
        const th = (2 + 9 * Math.sin(Math.PI * s)) * thin;
        for (let r = 0; r < th; r += c) {
          const col = r < c ? R[4] : r < 2 * c ? R[3] : R[2];
          if (r >= 2 * c && (i + Math.round(r / c)) % 2) continue;
          set(Math.round((6 + Math.cos(a) * (R0 - r)) / c) * c, Math.round((-34 + Math.sin(a) * (R0 - r) * 0.9) / c) * c, col, c);
        }
      }
    } else if (shape === 'line') {
      const x1 = Math.max(16, Db - 6);
      for (let x = 8; x <= x1; x += c) {
        const s = (x - 8) / (x1 - 8);
        if (s < s0 || s > s1) continue;
        const th = (9 - 5 * s) * thin;
        for (let r = 0; r < th; r += c) {
          const col = r < c ? R[4] : r < 2 * c ? R[3] : R[2];
          set(x, -45 + r, col, c);
          // A second, thinner band above (the layered qi lance of the refs).
          if (r < th * 0.4) set(x, -52 - r, r < c ? R[4] : R[3], c);
        }
        if (s > 0.85) set(x, -46, R[4], c);
        // A pale wash under the band, broken (dry brush).
        if (frame === 1 && (Math.round(x / c) % 3) !== 0) set(x, -45 + th + c, R[2], c);
      }
    } else {
      // A ring round the caster: the front half thicker; frame 0 the front, 1 all, 2 dithered.
      const n = 320;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        const front = Math.sin(a) > 0;
        if (frame === 0 && !front) continue;
        if (frame === 2 && i % 2) continue;
        const th = (front ? 7 : 3) * thin;
        for (let r = 0; r < th; r += c) {
          const col = r < c ? R[4] : r < 2 * c ? R[3] : R[2];
          set(Math.round((Math.cos(a) * (27 * S - r)) / c) * c, Math.round((-18 + Math.sin(a) * (11 * S - r * 0.4)) / c) * c, col, c);
        }
      }
    }
  });
}

/** The smear around blow `bi` at `tau` s from it (two frames before the contact, one after). */
function drawSmear(g: CanvasRenderingContext2D, tau: number, sc: InkScene, d: MoveDetail, bi: number): void {
  if (d.smear === 'none') return;
  const ring = d.smear === 'ring';
  if (tau < (ring ? -0.07 : -0.05) || tau >= (ring ? 0.1 : 0.05)) return;
  const frame: 0 | 1 | 2 = tau < (ring ? -0.03 : -0.02) ? 0 : tau < (ring ? 0.05 : 0.015) ? 1 : 2;
  const shape = d.smear === 'cross' ? (bi % 2 ? 'rise' : 'arc') : d.smear;
  drawPic(g, sc, smearPic(shape, RAMPS[d.ramp], sc.D, frame), 0, 0);
}

// ── 3. speed streaks and wakes ──

function drawWake(g: CanvasRenderingContext2D, tau: number, sc: InkScene, layer: InkLayer, d: MoveDetail, lead: number, w: DetailWeight): void {
  const R = RAMPS[d.ramp];
  const MR = RAMPS[d.motifRamp ?? d.ramp];
  if (d.wake === 'body') {
    if (tau < -0.08 || tau > 0.28 || layer !== 'over') return;
    const tt = tau + 0.08;
    const f = span(tau, 0.08, 0.28);
    const n = N([9, 12, 16], w);
    for (let i = 0; i < n; i++) {
      if (gone(i, f, 520)) continue;
      const y = -62 + 62 * hash2(i, 1, 521);
      const len = (6 + 12 * hash2(i, 2, 521)) * S;
      const x0 = -6 - 36 * S * tt - 8 * hash2(i, 4, 521);
      streak(g, sc, x0, y, len, R[4], R[3]);
    }
    // A few motifs shed behind, drifting down and back.
    for (let i = 0; i < N([4, 6, 8], w); i++) {
      if (gone(i, f, 522)) continue;
      const u = tt + i * 0.03;
      at(g, sc, d.motif, MR, d.motif === 'leaf' ? 11 : 3, 'world', -10 - 40 * S * u - i * 7, -50 + 16 * (i % 4) + 30 * u + Math.sin(u * 14 + i) * 4, Math.floor(u * 24 + i));
    }
    return;
  }
  // A dash: streaks along the path, appearing as the body passes, dust kicked at the feet.
  if (tau < -lead || tau > 0.4) return;
  const p = clamp01((tau + lead) / Math.max(0.05, lead));
  const f = span(tau, 0.12, 0.4);
  const n = N([12, 16, 22], w);
  if (layer === 'over')
    for (let i = 0; i < n; i++) {
      const x = sc.D * hash2(i, 1, 530);
      if (x > p * sc.D || gone(i, f, 531)) continue;
      const y = sc.tdy * (x / Math.max(1, sc.D)) - 64 + 64 * hash2(i, 2, 530);
      const len = (10 + 14 * hash2(i, 3, 530)) * 1.8;
      streak(g, sc, x, y, len, R[4], R[3]);
    }
  const m = N([3, 4, 6], w);
  for (let i = 0; i < m; i++) {
    const x = (sc.D * (i + 0.5)) / m;
    if (x > p * sc.D) continue;
    const age = tau + lead - (x / sc.D) * lead;
    const y = sc.tdy * (x / Math.max(1, sc.D));
    if (layer === 'ground') {
      // Dust kicked up where the feet passed, dissolving.
      const fd = Math.min(3, Math.floor(span(age, 0.1, 0.45) * 4));
      if (fd < 3) at(g, sc, 'puff', RAMPS.dust, 2 + (age > 0.12 ? 1 : 0), 'world', x - 4, y - 2 - age * 10, 0, fd);
    } else if (!gone(i, span(age, 0.3, 0.6), 533)) {
      at(g, sc, d.motif, MR, d.motif === 'leaf' ? 11 : 3, 'world', x - age * 20, y - 30 + age * 40, Math.floor(age * 20 + i));
    }
  }
}

/** A speed streak trailing back from (lx, ly), `len` art px: a light head and a body, two rects on the figures' grid. */
function streak(g: CanvasRenderingContext2D, sc: InkScene, lx: number, ly: number, len: number, head: string, body: string): void {
  const c = cellOf('fig');
  const [X, Y] = P(sc, lx, ly);
  const x = snap(X, 'fig');
  const y = snap(Y, 'fig');
  const L = snap(len, 'fig');
  const dir = sc.flip ? 1 : -1;
  g.fillStyle = head;
  g.fillRect(dir > 0 ? x : x - 2 * c, y, 2 * c, c);
  g.fillStyle = body;
  g.fillRect(dir > 0 ? x + 2 * c : x - L, y, L - 2 * c, c);
  drawn += 2;
}

// ── 4. spin: motifs flung round the caster ──

function drawSpin(g: CanvasRenderingContext2D, tau: number, sc: InkScene, layer: InkLayer, d: MoveDetail, w: DetailWeight): void {
  if (tau < 0 || tau > 0.85) return;
  const MR = RAMPS[d.motifRamp ?? d.ramp];
  const n = N([16, 22, 28], w);
  const f = span(tau, 0.45, 0.85);
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + tau * 7;
    const behind = Math.sin(a) < 0;
    if ((layer === 'ground') !== behind || gone(i, f, 540)) continue;
    const r = 16 + (34 + 10 * w) * S * easeOut(tau / 0.6) * (0.7 + 0.5 * hash2(i, 1, 540));
    const h = 14 + 50 * hash2(i, 2, 540) - 90 * tau * tau;
    const size = d.motif === 'leaf' ? 11 : d.motif === 'bead' ? 2 : 3 + (i % 3 === 0 ? 1 : 0);
    const x = Math.cos(a) * r;
    const y = -Math.max(0, h) + Math.sin(a) * r * 0.42;
    // A whirlwind: every third piece is dust kicked off the ground, the rest the art's motif.
    if (i % 3 === 0) at(g, sc, 'puff', RAMPS.dust, 3, 'world', x, Math.min(-2, y * 0.4), 0, Math.min(3, Math.floor(f * 4)));
    else at(g, sc, d.motif, MR, size, 'world', x, y, Math.floor(tau * 30 + i));
  }
}

// ── 5. body aura (buffs and heals) ──

const AURA_LIFE = 1.6;

function fanPic(R: Ramp, reveal: number, fade: number): Pic {
  const c = cellOf('fig');
  return pic(`fan|${R[0]}|${reveal}|${fade}|${c}`, -40, -130, 90, -10, (set) => {
    const ribs = 7;
    for (let i = 0; i < ribs; i++) {
      if (i > reveal) break;
      const a = -1.75 + (i / (ribs - 1)) * 1.35;
      for (let r = 2; r < 54; r += c) {
        if (fade && (i * 3 + Math.round(r / c)) % 4 < fade) continue;
        const x = Math.round((HAND[0] + Math.cos(a) * r) / c) * c;
        const y = Math.round((HAND[1] - 4 + Math.sin(a) * r) / c) * c;
        set(x, y, r > 42 ? R[2] : R[1], c);
      }
      // The leaf of the fan between this rib and the next: a pale band near the rim.
      if (i < ribs - 1 && i < reveal)
        for (let s = 0; s < 1; s += 0.12) {
          const b = a + (s * 1.35) / (ribs - 1);
          for (let r = 38; r < 53; r += c) {
            if (fade && (Math.round(s * 9) + Math.round(r / c)) % 3 < fade) continue;
            set(Math.round((HAND[0] + Math.cos(b) * r) / c) * c, Math.round((HAND[1] - 4 + Math.sin(b) * r) / c) * c, r > 51 ? R[0] : r > 45 ? R[3] : R[4], c);
          }
        }
    }
  });
}

function drawAura(g: CanvasRenderingContext2D, t: number, sc: InkScene, layer: InkLayer, d: MoveDetail): void {
  if (!d.aura || t < 0 || t > AURA_LIFE) return;
  const R = RAMPS[d.ramp];
  const MR = RAMPS[d.motifRamp ?? d.ramp];
  const f = span(t, 0.95, AURA_LIFE);
  switch (d.aura) {
    case 'rise': {
      // A breath (竹 / 息): the art's motif swirling round the body, drawn in on the inhale (0–0.55 s), held, then
      // released outward and up on the exhale; a soft qi mist rising; healing twinkles; a ring at the feet.
      const n = N([30, 30, 30], 0);
      const inhale = easeOut(span(t, 0, 0.55));
      const exhale = easeIn(span(t, 0.75, AURA_LIFE));
      const fadeAll = span(t, 1.15, AURA_LIFE);
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + t * 4.2 + (i % 2) * 0.3;
        const behind = Math.sin(a) < 0;
        if ((layer === 'ground') !== behind || gone(i, fadeAll, 550)) continue;
        const r = (34 - 20 * inhale + 30 * exhale) * 1.8 + (i % 3) * 3;
        const h = 8 + ((i * 7) % 5) * 18 + 40 * exhale;
        const size = d.motif === 'leaf' ? 11 + (i % 3) : 3 + (i % 2);
        at(g, sc, d.motif, MR, size, 'world', Math.cos(a) * r, -h + Math.sin(a) * r * 0.42, Math.floor(a * 2.5 + i));
      }
      if (layer === 'over') {
        // Qi mist rising from the feet, in the element's pale tones, dissolving as it climbs.
        for (let i = 0; i < N([14, 14, 14], 0); i++) {
          const lt = (t * 0.9 + i / 14) % 1;
          const fd = Math.min(3, Math.floor(lt * 4));
          if (fd >= 3 || t > AURA_LIFE - 0.1) continue;
          at(g, sc, 'mistPuff', R, 3, 'world', -26 + 52 * hash2(i, 1, 551), -4 - lt * 100, 0, fd);
        }
        // Healing twinkles: four-point stars that blink on and off round the body.
        const c = cellOf('world');
        for (let i = 0; i < N([12, 12, 12], 0); i++) {
          const ph = (t * 3 + hash2(i, 2, 552)) % 1;
          if (ph > 0.45 || fadeAll >= 1) continue;
          const [X, Y] = P(sc, -36 + 72 * hash2(i, 3, 552), -12 - 100 * hash2(i, 4, 552));
          const x = snap(X, 'world');
          const y = snap(Y, 'world');
          const arm = ph < 0.2 ? 3 : 2;
          g.fillStyle = R[3];
          g.fillRect(x - arm * c, y, (arm * 2 + 1) * c, c);
          g.fillRect(x, y - arm * c, c, (arm * 2 + 1) * c);
          g.fillStyle = '#ffffff';
          g.fillRect(x, y, c, c);
          drawn += 3;
        }
      } else if (t > 0.35 && t < 1.2) {
        // A light ring at the feet, swelling at the end of the inhale.
        const r = (16 + 8 * easeOut(span(t, 0.35, 1.2))) * S;
        drawPic(g, sc, ringPic(R, r, r * 0.42, Math.min(3, Math.floor(span(t, 0.8, 1.2) * 4)), 2), 0, 0);
      }
      return;
    }
    case 'swirl': {
      const n = N([22, 22, 22], 0);
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + t * 3.2 * (i % 2 ? 1 : -1);
        if ((layer === 'ground') !== Math.sin(a) < 0) continue;
        const hgt = [-10, -36, -62][i % 3]!;
        const r = (i % 2 ? 18 : 14) * S * (0.6 + 0.4 * easeOut(t / 0.3));
        const fd = Math.min(3, Math.floor(f * 4));
        if (fd >= 3 || t < (i / n) * 0.15) continue;
        at(g, sc, d.motif, MR, 4 + (i % 3 === 0 ? 1 : 0), 'world', Math.cos(a) * r, hgt + Math.sin(a) * r * 0.4 - t * 6, 0, fd);
      }
      if (d.wake && layer === 'over') drawWake(g, t - 0.1, sc, layer, d, 0, 0);
      return;
    }
    case 'fan': {
      if (layer !== 'over') return;
      const reveal = Math.floor(easeOut(span(t, 0, 0.3)) * 7);
      const fade = Math.min(3, Math.floor(f * 4));
      if (fade < 3) drawPic(g, sc, fanPic(R, reveal, fade), 0, 0);
      // Ink beads dripping from the rim, paper flecks lifting.
      for (let i = 0; i < N([5, 5, 5], 0); i++) {
        const lt = t - 0.3 - i * 0.08;
        if (lt < 0 || lt > 0.6) continue;
        const a = -1.75 + (i / 4) * 1.35;
        at(g, sc, 'bead', R, 1, 'fig', HAND[0] + Math.cos(a) * 52, HAND[1] - 4 + Math.sin(a) * 52 + 90 * lt * lt);
        if (d.alt) at(g, sc, d.alt, R, 1, 'fig', HAND[0] + Math.cos(a) * 30 + lt * 14, HAND[1] - 8 + Math.sin(a) * 30 - lt * 30);
      }
      return;
    }
    case 'converge': {
      if (layer === 'over') {
        const n = N([18, 18, 18], 0);
        const u = clamp01(t / 0.6);
        for (let i = 0; i < n && u < 1; i++) {
          const a = (i / n) * Math.PI * 2 + u * 3;
          const r = 38 * S * (1 - easeIn(u));
          at(g, sc, 'bead', R, i % 3 ? 2 : 1, 'world', Math.cos(a) * r, -60 + Math.sin(a) * r * 0.6);
        }
        if (t > 0.55 && t < 0.75) px(g, sc, R[4], -2, -62, 'fig', 5);
      } else {
        // Ripples on the ground at the feet once the mind settles.
        for (let k = 0; k < 2; k++) {
          const lt = t - 0.6 - k * 0.18;
          if (lt < 0 || lt > 0.6) continue;
          const r = Math.round((8 + 30 * easeOut(lt / 0.6)) * S);
          drawPic(g, sc, ringPic(R, r, Math.max(2, Math.round(r * 0.42)), Math.min(3, Math.floor(span(lt, 0.25, 0.6) * 4))), 0, 0);
        }
      }
      return;
    }
    case 'flame': {
      if (layer !== 'over') return;
      const c = cellOf('world');
      const n = N([26, 26, 26], 0);
      for (let i = 0; i < n; i++) {
        if (gone(i, f, 560)) continue;
        const cyc = (t * 2.2 + hash2(i, 1, 560)) % 1;
        const x = -28 + 56 * hash2(i, 2, 560) + Math.sin(t * 9 + i) * 2.5;
        const y = -6 - 96 * cyc - 10 * hash2(i, 3, 560);
        const h = Math.round((12 + 14 * (1 - cyc)) / c) * c;
        // A tongue: light tip, mid, base; a second, shorter column beside it (three + two rects).
        const [X, Y] = P(sc, x, y);
        const fx0 = snap(X, 'world');
        const fy0 = snap(Y, 'world');
        const mid = Math.round(h / 2 / c) * c;
        g.fillStyle = R[4];
        g.fillRect(fx0, fy0, c, c);
        g.fillStyle = R[3];
        g.fillRect(fx0, fy0 + c, c, mid - c);
        g.fillStyle = R[2];
        g.fillRect(fx0, fy0 + mid, c, h - mid);
        g.fillStyle = R[3];
        g.fillRect(fx0 + c, fy0 + 2 * c, c, mid - c);
        g.fillStyle = R[1];
        g.fillRect(fx0 + c, fy0 + mid + c, c, h - mid - 2 * c);
        drawn += 5;
      }
      for (let i = 0; i < N([8, 8, 8], 0); i++) {
        const a = (i / 8) * Math.PI * 2 + t * 4;
        if (gone(i, f, 561)) continue;
        at(g, sc, 'bead', R, 2, 'world', Math.cos(a) * 34, -30 - (i % 2) * 24 + Math.sin(a) * 14);
      }
      return;
    }
  }
}

// ── 6. impact ──

const MOTIF_SIZE: Readonly<Record<Motif, number>> = { leaf: 11, fluff: 3, puff: 3, mistPuff: 3, bead: 2, fleck: 2 };

/** Hit sparks by material, `tau` s after the blow, at the foe's chest. */
function drawSparks(g: CanvasRenderingContext2D, tau: number, sc: InkScene, layer: InkLayer, d: MoveDetail, w: DetailWeight): void {
  const R = RAMPS[d.ramp];
  const [fx, fy] = foeChest(sc);
  const n = N([14, 20, 28], w);
  const c = cellOf('world');
  const s = tau - 0.03;
  if (s < 0) return;
  switch (d.spark) {
    case 'steel': {
      if (s > 0.25 || layer !== 'over') return;
      for (let i = 0; i < n; i++) {
        const a = -0.9 + 1.8 * hash2(i, 1, 570) + (hash2(i, 4, 570) < 0.3 ? Math.PI : 0);
        const v = (90 + 80 * hash2(i, 2, 570)) * S * (1 + w * 0.25);
        const r = (v * (1 - Math.exp(-7 * s))) / 7;
        const len = Math.max(1, Math.round(8 * (1 - s / 0.25)));
        for (let k = 0; k < len; k++)
          px(g, sc, k === 0 ? '#ffffff' : k === 1 ? R[4] : R[3], fx + Math.cos(a) * (r - k * c), fy + Math.sin(a) * (r - k * c) + 140 * s * s, 'world');
      }
      return;
    }
    case 'qi': {
      if (s > 0.32 || layer !== 'over') return;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + hash2(i, 1, 571) * 0.5;
        const r = (8 + (14 + 5 * w) * S * easeOut(s / 0.32)) * (0.8 + 0.4 * hash2(i, 2, 571));
        const big = s < 0.16;
        px(g, sc, big ? R[4] : R[3], fx + Math.cos(a) * r, fy + Math.sin(a) * r * 0.7, 'world', big ? 3 : 2);
      }
      return;
    }
    case 'ink': {
      // Beads thrown in a fan; where one lands it leaves a stain (ground layer) that dries away.
      for (let i = 0; i < n; i++) {
        const a = -Math.PI / 2 + (hash2(i, 1, 572) - 0.3) * 2.4;
        const v = (60 + 60 * hash2(i, 2, 572)) * 1.8;
        const x = fx + Math.cos(a) * v * s * 0.8;
        const y = fy + Math.sin(a) * v * s * 0.8 + 260 * s * s;
        const floor = sc.tdy + 4 * hash2(i, 3, 572) - 2;
        if (y < floor) {
          if (layer === 'over') at(g, sc, 'bead', R, i % 3 ? 1 : 2, 'world', x, y);
        } else if (layer === 'ground') {
          // Where it came down: 260 s^2 + vy s + (fy - floor) = 0.
          const vy = Math.sin(a) * v * 0.8;
          const sl = (-vy + Math.sqrt(Math.max(0, vy * vy - 4 * 260 * (fy - floor)))) / (2 * 260);
          const fd = Math.floor(span(s, 0.6, 1.2) * 4);
          if (fd < 4) at(g, sc, 'bead', STAIN(R), 1, 'world', fx + Math.cos(a) * v * 0.8 * sl, floor, 0, fd);
        }
      }
      return;
    }
    case 'wind': {
      if (s > 0.36 || layer !== 'over') return;
      for (let i = 0; i < n; i++) {
        const a0 = -1.2 + 2.4 * hash2(i, 1, 573);
        const curl = (hash2(i, 2, 573) - 0.5) * 6;
        const r = (20 + 34 * hash2(i, 3, 573)) * S * easeOut(s / 0.36);
        // A wind ribbon: a curling streak, a light head two pixels thick.
        for (let k = 0; k < 12; k++) {
          const rr = r - k * 2.6;
          if (rr < 2) break;
          const a = a0 + curl * (rr / (50 * S));
          px(g, sc, k === 0 ? '#ffffff' : k < 3 ? R[4] : k < 6 ? R[3] : R[2], fx + Math.cos(a) * rr + s * 30, fy + Math.sin(a) * rr * 0.7, 'world', k < 4 ? 2 : 1);
        }
      }
      return;
    }
  }
}

/** The motif burst and dissipation: leaves flutter, fluff and puffs drift up, beads fall. */
function drawBurst(g: CanvasRenderingContext2D, tau: number, sc: InkScene, layer: InkLayer, d: MoveDetail, w: DetailWeight): void {
  const s = tau - 0.03;
  if (s < 0 || s > 1.1 || layer !== 'over') return;
  const MR = RAMPS[d.motifRamp ?? d.ramp];
  const [fx, fy] = foeChest(sc);
  const n = N([18, 24, 30], w);
  for (let i = 0; i < n; i++) {
    const m: Motif = d.alt && i % 3 === 2 ? d.alt : d.motif;
    const ramp = m === d.alt ? RAMPS[d.ramp] : MR;
    const a = -Math.PI / 2 + (hash2(i, 1, 580) - 0.5) * 2.8 + 0.3;
    const v = (40 + 50 * hash2(i, 2, 580)) * S * (1 + w * 0.2);
    const k = 1 - Math.exp(-5 * s);
    let x = fx + (Math.cos(a) * v * k) / 5;
    let y = fy + (Math.sin(a) * v * k) / 5;
    let fade = span(s, 0.55, 1.1);
    let fd = 0;
    let size = MOTIF_SIZE[m];
    switch (m) {
      case 'leaf':
        y += 36 * s * s;
        x += Math.sin(s * 9 + i) * 3 + s * 10;
        break;
      case 'fluff':
        y -= 14 * s;
        x += s * 12 + Math.sin(s * 7 + i) * 2;
        break;
      case 'puff':
      case 'mistPuff':
        size = 2 + Math.min(2, Math.floor(s * 6)) + (w > 1 ? 1 : 0);
        y -= 10 * s;
        fd = Math.min(3, Math.floor(span(s, 0.2, 0.7) * 4));
        fade = fd >= 3 ? 1 : 0;
        break;
      case 'bead':
        y += 200 * s * s;
        if (y > sc.tdy) continue;
        break;
      case 'fleck':
        y -= 20 * s;
        break;
    }
    if (fade >= 1 || gone(i, fade, 581)) continue;
    at(g, sc, m, ramp, size, 'world', x, y, Math.floor(s * 22 + i), fd);
  }
}

/** Ground: the ring and the mark the blow leaves. */
function drawMark(g: CanvasRenderingContext2D, tau: number, sc: InkScene, d: MoveDetail, w: DetailWeight): void {
  const R = RAMPS[d.ramp];
  const fx = foeX(sc);
  const fy = sc.tdy;
  if ((d.mark === 'ring' || w > 0) && tau >= 0.02 && tau < 0.45) {
    const r = Math.round(4 + (12 + 6 * w) * S * easeOut(tau / 0.3));
    drawPic(g, sc, ringPic(R, r, Math.max(2, Math.round(r * 0.42)), Math.min(3, Math.floor(span(tau, 0.2, 0.45) * 4)), w > 1 ? 2 : 1), fx, fy);
  }
  if (tau < 0.03 || tau > 1.5) return;
  const fd = Math.min(4, Math.floor(span(tau, 0.9, 1.5) * 4));
  if (fd >= 4) return;
  const c = cellOf('world');
  switch (d.mark) {
    case 'scuff':
      for (let i = 0; i < 4 + 2 * w; i++) {
        if (gone(i, fd / 4, 590)) continue;
        const y = fy - 4 + i * 2;
        const x = fx - 14 + 10 * hash2(i, 1, 590);
        for (let k = 0; k < 12 + 4 * w; k++) px(g, sc, k % 3 ? RAMPS.dust[2] : RAMPS.dust[1], x + k, y, 'world');
      }
      return;
    case 'scar': {
      const len = (10 + 4 * w) * S;
      for (let k = 0; k < len; k++) {
        if (gone(k, fd / 4, 591)) continue;
        const x = fx - len / 2 + k;
        const y = fy + 1 - Math.round(k * 0.25);
        px(g, sc, R[0], x, y, 'world');
        if (k % 2) px(g, sc, R[3], x, y - c, 'world');
      }
      return;
    }
    case 'ring':
      if (tau > 0.45) {
        const r = (16 + 6 * w) * S;
        drawPic(g, sc, ringPic(STAIN(R), r, Math.round(r * 0.42), Math.min(3, Math.max(1, fd))), fx, fy);
      }
      return;
    case 'stain':
      drawPic(g, sc, stainPic(R, 6 + 4 * w, fd), fx, fy);
      return;
    case 'none':
      return;
  }
}

/** A ground stain (an iso pool with a ragged, dithered rim), radius `rr`, dried by `fd` (0–3) dither steps. */
function stainPic(R: Ramp, rr: number, fd: number): Pic {
  const c = cellOf('world');
  return pic(`stain|${R[0]}|${rr}|${fd}|${c}`, -rr * 2 - 1, -rr - 1, rr * 2 + 2, rr + 2, (set) => {
    for (let y = -rr; y <= rr; y++)
      for (let x = -rr * 2; x <= rr * 2; x++) {
        const dd = Math.hypot(x / 2, y) + (hash2(x, y, 592) - 0.5) * 1.2;
        if (dd > rr || ((x + y) & 1 && dd > rr * 0.6) || gone(x * 31 + y, fd / 4, 593)) continue;
        set(x, Math.round(y * 0.5), dd > rr - 1 ? R[1] : R[0], c);
      }
  });
}

/** Pressure-point ripples on the foe's chest (a sealing jab). */
function drawRipple(g: CanvasRenderingContext2D, tau: number, sc: InkScene, kind: InkKind): void {
  const R = RAMPS.ink;
  const [acc] = tellOf(kind);
  const [fx, fy] = foeChest(sc);
  const c = cellOf('fig');
  for (let k = 0; k < 3; k++) {
    const lt = tau - k * 0.07;
    if (lt < 0 || lt > 0.35) continue;
    const u = easeOut(lt / 0.35);
    const rx = 2 + 7 * S * u;
    const ry = 3 + 11 * S * u;
    const n = Math.round((rx + ry) * 2 * quality);
    for (let i = 0; i < n; i++) {
      if (lt > 0.2 && i % 2) continue;
      const a = (i / n) * Math.PI * 2;
      px(g, sc, k === 0 ? acc : i % 2 ? R[1] : R[3], fx + Math.round((Math.cos(a) * rx) / c) * c, fy + Math.round((Math.sin(a) * ry) / c) * c, 'fig');
    }
  }
  if (tau < 0.1) px(g, sc, '#ffffff', fx - 1, fy - 1, 'fig', 4);
}

/** Blood beads drawn from the foe back into the caster's hand (lifesteal). */
function drawDrain(g: CanvasRenderingContext2D, tau: number, sc: InkScene, w: DetailWeight): void {
  const R = RAMPS.blood;
  const [fx, fy] = foeChest(sc);
  const n = N([14, 18, 22], w);
  for (let i = 0; i < n; i++) {
    const u = (tau - 0.1 - i * 0.03) / 0.45;
    if (u < 0 || u > 1) continue;
    const e = easeIn(u);
    const x = fx + (HAND[0] - fx) * e;
    const y = fy + (HAND[1] - fy) * e - Math.sin(u * Math.PI) * (14 + 6 * hash2(i, 1, 600));
    at(g, sc, 'bead', R, 2, 'fig', x, y);
    // A short wet trail behind each bead.
    for (let k = 1; k <= 2; k++) {
      const v = Math.max(0, u - k * 0.05);
      const ev = easeIn(v);
      px(g, sc, k === 1 ? R[3] : R[2], fx + (HAND[0] - fx) * ev, fy + (HAND[1] - fy) * ev - Math.sin(v * Math.PI) * (14 + 6 * hash2(i, 1, 600)), 'fig');
    }
    if (u > 0.92) px(g, sc, R[4], HAND[0], HAND[1], 'fig', 2);
  }
}

/** A cloud shockwave travelling from the palm to the foe over the lead (a ranged palm). */
function drawTravel(g: CanvasRenderingContext2D, tau: number, sc: InkScene, d: MoveDetail, lead: number): void {
  if (tau < -lead || tau > 0.05) return;
  const u = clamp01((tau + lead) / lead);
  const R = RAMPS[d.ramp];
  const x = 12 + (sc.D - 14) * easeIn(u);
  const y = -40 + sc.tdy * u;
  for (let i = -4; i <= 4; i++) at(g, sc, 'puff', R, 2 + (Math.abs(i) < 3 ? 1 : 0), 'world', x - Math.abs(i) * 5, y + i * 8);
  for (let k = 1; k <= 6; k++) px(g, sc, R[3], x - 10 - k * 8, y + ((k * 7) % 9) - 4, 'world', 2);
}

/** A ring of the element at the target spot, drawing in (a ground-aimed area), with chill specks falling. */
function drawSpot(g: CanvasRenderingContext2D, tau: number, sc: InkScene, layer: InkLayer, d: MoveDetail): void {
  if (tau < 0 || tau > 1) return;
  const MR = RAMPS[d.motifRamp ?? d.ramp];
  const n = N([22, 22, 22], 0);
  const r = (30 - 8 * easeOut(tau / 0.6)) * S;
  const fd = Math.min(3, Math.floor(span(tau, 0.55, 1) * 4));
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + tau * 1.5;
    if ((layer === 'ground') !== Math.sin(a) < 0) continue;
    at(g, sc, d.motif, MR, 3 + (i % 2), 'world', foeX(sc) + Math.cos(a) * r, sc.tdy - 4 + Math.sin(a) * r * 0.42, 0, fd);
  }
  // The ring's line on the ground, closing in with the puffs.
  if (layer === 'ground' && fd < 3) drawPic(g, sc, ringPic(MR, Math.round(r), Math.round(r * 0.42), fd, 2), foeX(sc), sc.tdy - 4);
  if (layer === 'over')
    for (let i = 0; i < N([16, 16, 16], 0); i++) {
      const lt = (tau * 1.6 + hash2(i, 1, 610)) % 1;
      if (gone(i, span(tau, 0.6, 1), 611)) continue;
      px(g, sc, RAMPS.mist[4], foeX(sc) - 50 + 100 * hash2(i, 2, 610), sc.tdy - 90 + 86 * lt, 'world', 2);
    }
}

// ── the move ──

/**
 * A move's caster-side detail at `t` s from its release, its blows `contact` s after it (chains `d.chain` later):
 * the tell before the first blow, the smears, wakes, spins, auras, the travelling shockwave and the spot ring.
 */
export function drawMoveDetail(g: CanvasRenderingContext2D, t: number, sc: InkScene, layer: InkLayer, d: MoveDetail, kind: InkKind, contact: number, w: DetailWeight): void {
  if (d.aura) {
    drawAuraBloom(g, t, sc, layer, RAMPS[d.motifRamp ?? d.ramp], kind === 'heal', AURA_LIFE);
    drawAura(g, t, sc, layer, d);
    if (kind === 'heal' || kind === 'buff') return;
  }
  const lead = Math.max(0.12, Math.min(0.35, contact));
  const tau = t - contact;
  if (layer === 'over') drawTell(g, (tau + lead) / lead, sc, d, kind, w);
  const blows = [0, ...(d.chain ?? [])];
  if (layer === 'over') blows.forEach((b, i) => drawSmear(g, tau - b, sc, d, i));
  if (d.wake) drawWake(g, tau, sc, layer, d, lead, w);
  if (d.spin)
    blows.forEach((b) => {
      drawSpinBloom(g, tau - b, sc, layer, RAMPS[d.ramp], w);
      drawSpin(g, tau - b, sc, layer, d, w);
    });
  if (d.travel && layer === 'over') drawTravel(g, tau, sc, d, lead);
  if (d.spot) drawSpot(g, tau, sc, layer, d);
}

/** A charged move's wind-up (`u` 0–1 over its `windup`): the element motes and the kind's "now" glint. */
export function drawCastDetail(g: CanvasRenderingContext2D, u: number, sc: InkScene, layer: InkLayer, d: MoveDetail, kind: InkKind, w: DetailWeight): void {
  if (layer === 'over') drawTell(g, u, sc, d, kind, w, [10, -52]);
}

/** A landed blow's detail, `tau` s after it: sparks by material, the motif burst, the ring and mark, the extras. */
export function drawHitDetail(g: CanvasRenderingContext2D, tau: number, sc: InkScene, layer: InkLayer, d: MoveDetail, kind: InkKind, w: DetailWeight): void {
  if (tau < 0 || tau > 1.6) return;
  drawHitBloom(g, tau, sc, layer, RAMPS[d.ramp], w);
  drawSparks(g, tau, sc, layer, d, w);
  drawBurst(g, tau, sc, layer, d, w);
  if (layer === 'ground') drawMark(g, tau, sc, d, w);
  if (d.ripple && layer === 'over') drawRipple(g, tau, sc, kind);
  if (d.drain && layer === 'over') drawDrain(g, tau, sc, w);
}

/** How long the caster-side and the hit detail run (s). */
export const detailLife = (d: MoveDetail, contact: number): number =>
  Math.max(d.aura ? AURA_LIFE : 0, contact + (d.chain?.[d.chain.length - 1] ?? 0) + (d.spin ? 0.9 : d.spot ? 1 : 0.45));
export const HIT_LIFE = 1.6;

// ── warps (a chain of warp strikes: C0-SKILLFX showcase) ──

/**
 * One warp of a warp chain, `tau` s after the fighter vanished from `from` and appeared at `to` (local art px, feet):
 * a qi streak from the old place to the new (head out fast, tail wiped), specks scattering where they vanished, dust at
 * the old feet, a flash and a closing ring where they appear.
 */
export function drawWarpBeat(g: CanvasRenderingContext2D, tau: number, from: readonly [number, number], to: readonly [number, number], sc: InkScene, layer: InkLayer, ramp: RampId): void {
  if (tau < 0 || tau > 0.32) return;
  const R = RAMPS[ramp];
  if (layer === 'ground') {
    if (tau < 0.26) {
      const r = (22 - 14 * easeOut(tau / 0.2)) * S;
      drawPic(g, sc, ringPic(R, r, r * 0.42, Math.min(3, Math.floor(span(tau, 0.12, 0.26) * 4)), 2), to[0], to[1]);
    }
    const fd = Math.min(3, Math.floor(span(tau, 0.05, 0.3) * 4));
    if (fd < 3) at(g, sc, 'puff', RAMPS.dust, 3, 'world', from[0], from[1] - 2 - tau * 12, 0, fd);
    return;
  }
  const head = easeOut(span(tau, 0, 0.06));
  const tail = span(tau, 0.04, 0.18);
  for (const [dy, big] of [[-30, true], [-18, false], [-44, false]] as const) {
    const n = 16;
    for (let i = 0; i <= n; i++) {
      const s = i / n;
      if (s > head || s < tail) continue;
      const x = from[0] + (to[0] - from[0]) * s;
      const y = from[1] + (to[1] - from[1]) * s + dy;
      px(g, sc, s > head - 0.12 ? '#ffffff' : i % 2 ? R[4] : R[3], x, y, 'world', big ? 2 : 1);
    }
  }
  // Specks scattering where they vanished.
  if (tau < 0.22)
    for (let i = 0; i < N([8, 8, 8], 0); i++) {
      const a = (i / 8) * Math.PI * 2;
      const r = 4 + 26 * S * easeOut(tau / 0.22);
      if (gone(i, span(tau, 0.1, 0.22), 620)) continue;
      px(g, sc, i % 2 ? R[3] : R[4], from[0] + Math.cos(a) * r * 0.6, from[1] - 30 + Math.sin(a) * r, 'world', 2);
    }
  // The flash where they appear.
  if (tau < 0.08) {
    const c = cellOf('world');
    const [X, Y] = P(sc, to[0], to[1] - 32);
    const x = snap(X, 'world');
    const y = snap(Y, 'world');
    const arm = tau < 0.04 ? 12 : 6;
    g.fillStyle = R[4];
    g.fillRect(x - arm * c, y, (arm * 2 + 1) * c, c);
    g.fillRect(x, y - arm * c, c, (arm * 2 + 1) * c);
    g.fillStyle = '#ffffff';
    g.fillRect(x - c, y - c, 3 * c, 3 * c);
    drawn += 3;
  }
}
